import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility, VisibilityOff, Print, FileDownload } from "@mui/icons-material";
import ClearFiltersButton from "../components/ClearFiltersButton";
import DonutChart from "../components/charts/DonutChart";
import ReportSiblingNav from "../components/Reports/ReportSiblingNav";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import { FooterInternal } from "../components/Footer";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  reportsService,
  ScheduleAnalysisResponse,
  ReportProfessionalItem,
} from "../services/reportsService";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import MonthSelect from "../components/Inputs/MonthSelect";

// Análise de Atendimentos — lê o espelho do Bubble via API (read-only).
// Nomes de profissionais mascarados por padrão (tela e impressão); "Exibir nomes" alterna.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[parseInt(monthNumber, 10) - 1]}/${year.slice(2)}`;
};

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
};

// Cores dos painéis: pago/pendente seguem os cards; formas e planos numa paleta fixa e distinta
const PAID_COLOR = "#28a745";
const PENDING_COLOR = "#dc3545";
const SHARE_PALETTE = ["#03B4C6", "#4682B4", "#fd7e14", "#6f42c1", "#20c997", "#e83e8c", "#adb5bd"];

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const ReportScheduleAnalysis: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();

  // filtros vindos de outra página de relatório (navegação preserva o período)
  const paramFrom = getParam("from");
  const paramTo = getParam("to");
  const initialBounds = competenceBounds(currentCompetence());
  const [competence, setCompetence] = useState<string>(
    paramFrom && paramTo ? getParam("competence") ?? "" : currentCompetence()
  );
  const [fromDate, setFromDate] = useState<string>(paramFrom ?? initialBounds.from);
  const [toDate, setToDate] = useState<string>(paramTo ?? initialBounds.to);
  const [selectedProfessionals, setSelectedProfessionals] = useState<string[]>([]);
  const [showInactiveProfessionals, setShowInactiveProfessionals] = useState(false);
  const [showNames, setShowNames] = useState<boolean>(false);

  const [professionals, setProfessionals] = useState<ReportProfessionalItem[]>([]);
  const visibleProfessionals = professionals.filter((p) => (p.active ?? true) || showInactiveProfessionals);
  const [data, setData] = useState<ScheduleAnalysisResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const displayName = (name: string): string => (showNames ? name : maskName(name));

  // filtros reativos: qualquer alteração dispara a consulta imediatamente (padrão da Agenda)
  const defaultBounds = competenceBounds(currentCompetence());
  const hasFilterChanges =
    competence !== currentCompetence() ||
    fromDate !== defaultBounds.from ||
    toDate !== defaultBounds.to ||
    selectedProfessionals.length > 0;

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) {
      const bounds = competenceBounds(value);
      setFromDate(bounds.from);
      setToDate(bounds.to);
      loadData({ from: bounds.from, to: bounds.to, professionalIds: selectedProfessionals });
    }
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setCompetence("");
    if (value && toDate) {
      loadData({ from: value, to: toDate, professionalIds: selectedProfessionals });
    }
  };

  const handleToDateChange = (value: string) => {
    setToDate(value);
    setCompetence("");
    if (fromDate && value) {
      loadData({ from: fromDate, to: value, professionalIds: selectedProfessionals });
    }
  };

  const handleProfessionalsChange = (ids: string[]) => {
    setSelectedProfessionals(ids);
    loadData({ from: fromDate, to: toDate, professionalIds: ids });
  };

  const toggleInactiveProfessionals = () => {
    setShowInactiveProfessionals((prev) => {
      const next = !prev;
      if (!next) {
        const onlyActive = selectedProfessionals.filter(
          (id) => professionals.find((p) => p.externalId === id)?.active ?? true,
        );
        if (onlyActive.length !== selectedProfessionals.length) {
          handleProfessionalsChange(onlyActive);
        }
      }
      return next;
    });
  };

  const handleSelectAllProfessionals = () => {
    const ids =
      selectedProfessionals.length === visibleProfessionals.length
        ? []
        : visibleProfessionals.map((p) => p.externalId);
    handleProfessionalsChange(ids);
  };

  const loadData = useCallback(
    async (override?: { from: string; to: string; professionalIds: string[] }) => {
      const params = override ?? { from: fromDate, to: toDate, professionalIds: selectedProfessionals };
      if (!params.from || !params.to) return;
      setLoading(true);
      setError("");
      try {
        const response = await reportsService.getScheduleAnalysis(params);
        setData(response);
      } catch {
        setError("Não foi possível carregar os dados. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, selectedProfessionals]
  );

  const clearFilters = () => {
    const competenceNow = currentCompetence();
    const bounds = competenceBounds(competenceNow);
    setCompetence(competenceNow);
    setFromDate(bounds.from);
    setToDate(bounds.to);
    setSelectedProfessionals([]);
    loadData({ from: bounds.from, to: bounds.to, professionalIds: [] });
  };

  const handlePrint = () => {
    // impressão sempre com nomes mascarados, independentemente do estado da tela
    const wasShowing = showNames;
    setShowNames(false);
    setTimeout(() => {
      window.print();
      setShowNames(wasShowing);
    }, 150);
  };

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const blob = await reportsService.exportXlsx({
        from: fromDate,
        to: toDate,
        professionalIds: selectedProfessionals,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `atendimentos_${fromDate}_a_${toDate}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    reportsService.getProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  useEffect(() => {
    loadData();
    // carga inicial apenas; recargas via botão Aplicar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxMonthlyTotal = useMemo(
    () => Math.max(1, ...(data?.monthly.map((m) => m.totalSchedules) ?? [1])),
    [data]
  );

  const totalsCards = data
    ? [
        { label: "Agendamentos", value: String(data.totals.totalSchedules) },
        { label: "Realizados", value: String(data.totals.attended), color: "#32CD32" },
        { label: "Faltas cobráveis", value: String(data.totals.chargeableNoShows), color: "#009900" },
        { label: "Cancelados", value: String(data.totals.cancelled), color: "#FF6347" },
        { label: "Comparecimento", value: `${data.totals.attendanceRate}%` },
        { label: "Valor realizado", value: formatCurrency(data.totals.realizedValue), color: colors.primary },
        {
          label: "Valor pago",
          value: formatCurrency(data.totals.paidValue ?? 0),
          color: "#28a745",
          note:
            (data.totals.advancePaidValue ?? 0) >= 0.01
              ? `+${formatCurrency(data.totals.advancePaidValue ?? 0)} antecipados`
              : undefined,
        },
        {
          label: "Valor pendente",
          value: formatCurrency(data.totals.pendingValue ?? 0),
          color: (data.totals.pendingValue ?? 0) >= 0.01 ? "#dc3545" : undefined,
        },
        { label: "Repasse Previsto", value: formatCurrency(data.totals.professionalPayout) },
        { label: "Ticket médio", value: formatCurrency(data.totals.averageTicket) },
      ]
    : [];

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    py: 0.75,
    borderBottom: "1px solid #f1f3f5",
    flexWrap: "wrap" as const,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Impressão: só o conteúdo do relatório (header/rodapé/filtros/botões ocultos) */}
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabelas largas: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Análise de Atendimentos
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Fonte: agenda (sincronizada de hora em hora). Receita realizada considera presenças
                cobráveis; repasse previsto considera apenas atendimentos realizados.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={handleCompetenceChange}
                  sx={filterFieldSx}
                />
                <TextField
                  size="small"
                  type="date"
                  label="De"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Até"
                  value={toDate}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              <TextField
                select
                size="small"
                label="Profissionais"
                value={selectedProfessionals}
                onChange={(e) => {
                  const value = e.target.value;
                  const ids = (typeof value === "string" ? value.split(",") : value).filter((v) => v !== "all");
                  handleProfessionalsChange(ids);
                }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const ids = selected as string[];
                    if (ids.length === 0) return "Todos";
                    if (ids.length > 2) return `${ids.length} selecionados`;
                    return professionals
                      .filter((p) => ids.includes(p.externalId))
                      .map((p) => displayName(p.name))
                      .join(", ");
                  },
                  displayEmpty: true,
                }}
                fullWidth
                sx={filterFieldSx}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem
                  value="all"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectAllProfessionals();
                  }}
                  sx={{
                    backgroundColor: "#f8f9fa !important",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  <Checkbox
                    checked={visibleProfessionals.length > 0 && selectedProfessionals.length === visibleProfessionals.length}
                    indeterminate={selectedProfessionals.length > 0 && selectedProfessionals.length < visibleProfessionals.length}
                    sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                  />
                  <ListItemText
                    primary={`Selecionar Todos (${visibleProfessionals.length})`}
                    primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                  />
                </MenuItem>
                {/* Inativos sob demanda, como na Agenda: histórico de quem saiu */}
                <MenuItem
                  value="__inactive"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleInactiveProfessionals();
                  }}
                  sx={{
                    backgroundColor: "#f8f9fa !important",
                    fontSize: "0.85rem",
                    borderBottom: "1px solid #e9ecef",
                  }}
                >
                  <Checkbox
                    checked={showInactiveProfessionals}
                    sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                  />
                  <ListItemText primary="Mostrar inativos" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }} />
                </MenuItem>
                {visibleProfessionals.map((p) => (
                  <MenuItem key={p.externalId} value={p.externalId}>
                    <Checkbox size="small" checked={selectedProfessionals.includes(p.externalId)} />
                    <ListItemText primary={displayName(p.name) + (p.active === false ? " (inativo)" : "")} primaryTypographyProps={{ fontSize: 14 }} />
                  </MenuItem>
                ))}
              </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title={showNames ? "Ocultar nomes" : "Exibir nomes"}>
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={() => setShowNames((v) => !v)}
                      aria-label={showNames ? "Ocultar nomes" : "Exibir nomes"}
                    >
                      {showNames ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Imprimir (nomes sempre mascarados)">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handlePrint}
                      disabled={loading || !data}
                      aria-label="Imprimir"
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar planilha: um atendimento por linha, com serviço, plano, valores, desconto e pagamento">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handleExport}
                      disabled={exporting || loading}
                      aria-label="Exportar planilha"
                    >
                      {exporting ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : <FileDownload fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <ReportSiblingNav current="report-schedule-analysis" competence={competence} from={fromDate} to={toDate} />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && data.totals.totalSchedules === 0 && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhum atendimento encontrado para os filtros selecionados.
            </Box>
          )}

          {!loading && !error && data && data.totals.totalSchedules > 0 && (
            <>
              {/* Cards de resumo */}
              <Box sx={summaryCardsRowSx}>
                {totalsCards.map((card) => (
                  <ReportSummaryCard key={card.label} {...card} />
                ))}
              </Box>

              {/* Painéis: pago × pendente, por forma de pagamento, Particular × Convênio */}
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box sx={{ ...sectionStyle, flex: "1 1 300px", minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Pago × pendente
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1.5 }}>
                    Sessões cobráveis do período
                  </Typography>
                  <DonutChart
                    segments={[
                      { label: "Pago", value: data.totals.paidValue ?? 0, color: PAID_COLOR },
                      { label: "Pendente", value: data.totals.pendingValue ?? 0, color: PENDING_COLOR, sub: `${data.totals.pendingCount ?? 0} sessões` },
                    ]}
                    centerValue={formatCurrency((data.totals.paidValue ?? 0) + (data.totals.pendingValue ?? 0))}
                    centerLabel="cobrável"
                    formatValue={formatCurrency}
                  />
                </Box>
                <Box sx={{ ...sectionStyle, flex: "1 1 300px", minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Pagamentos por forma
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1.5 }}>
                    Sessões do período já pagas, pela forma do recibo
                  </Typography>
                  <DonutChart
                    segments={(data.byPaymentType ?? []).map((item, index) => ({
                      label: item.name,
                      value: item.value,
                      color: SHARE_PALETTE[index % SHARE_PALETTE.length],
                      sub: `${item.count} sessões`,
                    }))}
                    centerValue={formatCurrency((data.byPaymentType ?? []).reduce((sum, item) => sum + item.value, 0))}
                    centerLabel="pago"
                    formatValue={formatCurrency}
                    emptyText="Nenhuma sessão do período foi paga ainda"
                  />
                </Box>
                <Box sx={{ ...sectionStyle, flex: "1 1 300px", minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Particular × Convênio
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 1.5 }}>
                    Atendimentos realizados, por origem do serviço
                  </Typography>
                  <DonutChart
                    segments={(data.byOrigin ?? []).map((item) => ({
                      label: item.origin,
                      value: item.count,
                      color: item.origin === "Convênio" ? "#6f42c1" : colors.primary,
                      sub: formatCurrency(item.value),
                    }))}
                    centerValue={String((data.byOrigin ?? []).reduce((sum, item) => sum + item.count, 0))}
                    centerLabel="realizados"
                  />
                </Box>
              </Box>

              {/* Por convênio/plano: realizados, valor, pago e pendente, com a barra pago/pendente na linha */}
              {(data.byPlan ?? []).length > 0 && (
                <Box sx={sectionStyle}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Por convênio/plano — realizados × valor × pago/pendente
                  </Typography>
                  <Box sx={{ overflowX: "auto" }}>
                    <Box sx={{ minWidth: 760 }}>
                      <Box sx={{ ...rowStyle, flexWrap: "nowrap", borderBottom: "2px solid #dee2e6", color: "#6c757d", fontSize: 12 }}>
                        <Typography sx={{ flex: 2, fontSize: 12, minWidth: 160 }}>Plano</Typography>
                        <Typography sx={{ width: 90, fontSize: 12 }}>Origem</Typography>
                        <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Realizados</Typography>
                        <Typography sx={{ width: 115, textAlign: "right", fontSize: 12 }}>Valor</Typography>
                        <Typography sx={{ width: 115, textAlign: "right", fontSize: 12 }}>Pago</Typography>
                        <Typography sx={{ width: 115, textAlign: "right", fontSize: 12 }}>Pendente</Typography>
                        <Typography sx={{ width: 150, textAlign: "right", fontSize: 12 }}>% pago</Typography>
                      </Box>
                      {(data.byPlan ?? []).map((item) => {
                        const base = item.paidValue + item.pendingValue;
                        const paidPct = base > 0 ? (100 * item.paidValue) / base : 0;
                        return (
                          <Box key={`${item.plan}|${item.origin}`} sx={{ ...rowStyle, flexWrap: "nowrap" }}>
                            <Typography sx={{ flex: 2, fontSize: 14, minWidth: 160 }}>{item.plan}</Typography>
                            <Typography sx={{ width: 90, fontSize: 13, color: item.origin === "Convênio" ? "#6f42c1" : colors.primary }}>
                              {item.origin}
                            </Typography>
                            <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                              {item.attended}
                            </Typography>
                            <Typography sx={{ width: 115, textAlign: "right", fontSize: 14 }}>{formatCurrency(item.realizedValue)}</Typography>
                            <Typography sx={{ width: 115, textAlign: "right", fontSize: 14, color: PAID_COLOR }}>{formatCurrency(item.paidValue)}</Typography>
                            <Typography sx={{ width: 115, textAlign: "right", fontSize: 14, color: item.pendingValue >= 0.01 ? PENDING_COLOR : undefined }}>
                              {formatCurrency(item.pendingValue)}
                            </Typography>
                            <Box sx={{ width: 150, display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                              <Box sx={{ flex: 1, height: 8, borderRadius: 4, background: PENDING_COLOR, overflow: "hidden", opacity: base > 0 ? 1 : 0.25 }}>
                                <Box sx={{ width: `${paidPct}%`, height: "100%", background: PAID_COLOR }} />
                              </Box>
                              <Typography sx={{ fontSize: 12, width: 44, textAlign: "right" }}>{paidPct.toFixed(0)}%</Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {/* Por status da agenda */}
                <Box sx={{ ...sectionStyle, flex: "1 1 340px" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Atendimentos por status da agenda
                  </Typography>
                  {data.byStatus.map((item) => (
                    <Box key={item.status} sx={rowStyle}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: item.color || "#adb5bd" }} />
                      <Typography sx={{ fontSize: 14, flex: 1 }}>{item.status}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                        {item.count}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#6c757d", width: 48, textAlign: "right" }}>
                        {((100 * item.count) / data.totals.totalSchedules).toFixed(1)}%
                      </Typography>
                      <Typography sx={{ fontSize: 13, width: 110, textAlign: "right", color: colors.textPrimary }}>
                        {formatCurrency(item.grossValue)}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Por presença */}
                <Box sx={{ ...sectionStyle, flex: "1 1 340px" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                    Atendimentos por presença
                  </Typography>
                  {data.byPresence.map((item) => (
                    <Box key={item.status} sx={rowStyle}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", background: item.color || "#adb5bd" }} />
                      <Typography sx={{ fontSize: 14, flex: 1 }}>{item.status}</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                        {item.count}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#6c757d", width: 48, textAlign: "right" }}>
                        {((100 * item.count) / data.totals.totalSchedules).toFixed(1)}%
                      </Typography>
                      <Box sx={{ width: 150, textAlign: "right" }}>
                        <Typography sx={{ fontSize: 13, color: colors.textPrimary }}>
                          {formatCurrency(item.grossValue)}
                        </Typography>
                        {item.attended && (
                          <Typography sx={{ fontSize: 11, color: colors.primary }}>
                            líquido {formatCurrency(item.grossValue - item.payoutValue)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Por profissional */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  Por profissional — quantidade × valor realizado × repasse previsto
                </Typography>
                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 900 }}>
                    <Box sx={{ ...rowStyle, flexWrap: "nowrap", borderBottom: "2px solid #dee2e6", color: "#6c757d", fontSize: 12 }}>
                      <Typography sx={{ flex: 2, fontSize: 12, minWidth: 150 }}>Profissional</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Agendados</Typography>
                      <Typography sx={{ width: 80, textAlign: "right", fontSize: 12 }}>Realizados</Typography>
                      <Typography sx={{ width: 90, textAlign: "right", fontSize: 12 }}>Canc. paciente</Typography>
                      <Typography sx={{ width: 100, textAlign: "right", fontSize: 12 }}>Canc. profissional</Typography>
                      <Typography sx={{ width: 90, textAlign: "right", fontSize: 12 }}>Não atendidos</Typography>
                      <Typography sx={{ width: 115, textAlign: "right", fontSize: 12 }}>Valor realizado</Typography>
                      <Typography sx={{ width: 115, textAlign: "right", fontSize: 12 }}>Repasse Previsto</Typography>
                    </Box>
                    {data.byProfessional.map((item) => (
                      <Box key={item.professionalExternalId || item.professionalName} sx={{ ...rowStyle, flexWrap: "nowrap" }}>
                        <Typography sx={{ flex: 2, fontSize: 14, minWidth: 150 }}>{displayName(item.professionalName)}</Typography>
                        <Typography sx={{ width: 80, textAlign: "right", fontSize: 14, fontWeight: typography.fontWeight.semibold }}>
                          {item.totalSchedules}
                        </Typography>
                        <Typography sx={{ width: 80, textAlign: "right", fontSize: 14 }}>{item.attended}</Typography>
                        <Typography sx={{ width: 90, textAlign: "right", fontSize: 14, color: "#FF6347" }}>
                          {item.cancelledByPatient}
                        </Typography>
                        <Typography sx={{ width: 100, textAlign: "right", fontSize: 14, color: "#512E5F" }}>
                          {item.cancelledByProfessional}
                        </Typography>
                        <Typography sx={{ width: 90, textAlign: "right", fontSize: 14, color: "#6c757d" }}>
                          {item.notAttended}
                        </Typography>
                        <Typography sx={{ width: 115, textAlign: "right", fontSize: 14 }}>
                          {formatCurrency(item.realizedValue)}
                        </Typography>
                        <Typography sx={{ width: 115, textAlign: "right", fontSize: 14, color: colors.primary }}>
                          {formatCurrency(item.professionalPayout)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Evolução mensal (aparece quando o período cobre mais de um mês) */}
              {data.monthly.length > 1 && (
                <Box sx={sectionStyle}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                    Evolução mensal
                  </Typography>
                  {data.monthly.map((m) => (
                    <Box key={m.month} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography sx={{ width: 58, fontSize: 12, color: "#6c757d" }}>
                        {formatMonthLabel(m.month)}
                      </Typography>
                      <Box sx={{ flex: 1, background: "#f1f3f5", borderRadius: 1, height: 22, position: "relative" }}>
                        <Box
                          sx={{
                            width: `${(100 * m.totalSchedules) / maxMonthlyTotal}%`,
                            background: "#cfeef2",
                            height: "100%",
                            borderRadius: 1,
                            position: "absolute",
                          }}
                        />
                        <Box
                          sx={{
                            width: `${(100 * m.attended) / maxMonthlyTotal}%`,
                            background: colors.primary,
                            height: "100%",
                            borderRadius: 1,
                            position: "absolute",
                          }}
                        />
                        <Typography sx={{ position: "relative", fontSize: 11, px: 1, lineHeight: "22px", color: "#2D3748" }}>
                          {m.attended}/{m.totalSchedules} · {formatCurrency(m.realizedValue)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Chip size="small" label="Realizados" sx={{ background: colors.primary, color: "#fff" }} />
                    <Chip size="small" label="Agendados" sx={{ background: "#cfeef2" }} />
                  </Box>
                </Box>
              )}
            </>
          )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportScheduleAnalysis;
